import React, { useEffect, useState, useRef } from "react";
import {
  ref,
  get,
  set,
  push,
  onValue,
  serverTimestamp,
} from "firebase/database";
import { database } from "../../firebase";
import TopBar from "../Sidebar/SidebarUser";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";

// Utility to convert email to valid Firebase key
const formatEmailKey = (email) =>
  email.replace(/@/g, "_AT_").replace(/\./g, "_DOT_");

const Chat = () => {
  const [chatRoomKey, setChatRoomKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [input, setInput] = useState("");

  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages update
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  // On mount: create or load chat room for current user
  useEffect(() => {
    const storedUser = localStorage.getItem("currentUser");
    if (!storedUser) {
      setError("No logged in user found.");
      setLoading(false);
      return;
    }

    const user = JSON.parse(storedUser);
    if (!user.email) {
      setError("User email not found.");
      setLoading(false);
      return;
    }

    const userEmail = user.email;
    const roomKey = formatEmailKey(userEmail);
    setChatRoomKey(roomKey);

    const roomRef = ref(database, `ChatAdminRooms/${roomKey}`);

    // Check if room exists
    get(roomRef)
      .then((snapshot) => {
        if (!snapshot.exists()) {
          // Create room if missing
          const newRoom = {
            participants: {
              user: userEmail,
              admin: "Nobunaga-2001", // hardcoded admin for now
            },
            lastUpdated: new Date().toISOString(),
            messages: {},
          };
          return set(roomRef, newRoom).then(() => newRoom);
        }
        return snapshot.val();
      })
      .then((room) => {
        setParticipants(room.participants || null);
        // Listen for message updates live
        onValue(ref(database, `ChatAdminRooms/${roomKey}/messages`), (msgSnap) => {
          const msgs = msgSnap.val() || {};
          // Convert to sorted array
          const sorted = Object.entries(msgs).sort(
            ([, a], [, b]) => new Date(a.timestamp) - new Date(b.timestamp)
          );
          setMessages(sorted);
          setLoading(false);
        });
      })
      .catch((err) => {
        setError("Failed to load or create chat room: " + err.message);
        setLoading(false);
      });
  }, []);

  const handleSendMessage = () => {
    if (!input.trim() || !chatRoomKey) return;

    const userEmail = JSON.parse(localStorage.getItem("currentUser")).email;

    const messagesRef = ref(database, `ChatAdminRooms/${chatRoomKey}/messages`);
    // Push new message
    push(messagesRef, {
      sender: userEmail,
      text: input.trim(),
      timestamp: new Date().toISOString(),
      status: "sent",
    })
      .then(() => {
        setInput("");
      })
      .catch((err) => {
        setError("Failed to send message: " + err.message);
      });
  };

  if (loading)
    return (
      <>
        <TopBar />
        <Box sx={{ mt: 8, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      </>
    );

  if (error)
    return (
      <>
        <TopBar />
        <Box sx={{ mt: 8, color: "red", textAlign: "center" }}>{error}</Box>
      </>
    );

  return (
    <>
      <TopBar />
      <Box
        sx={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f7fa',
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 2,
            bgcolor: "#fff",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mb: 2,
          }}
        >
          {messages.length === 0 && (
            <Typography sx={{ textAlign: "center", color: "#999" }}>
              No messages yet.
            </Typography>
          )}

          {messages.map(([msgId, msg]) => (
            <Box
              key={msgId}
              sx={{
                maxWidth: "80%",
                bgcolor: msg.sender === participants.user ? "#d1f0d1" : "#e1eaff",
                borderRadius: 2,
                p: 1.5,
                alignSelf: msg.sender === participants.user ? "flex-end" : "flex-start", // User's messages are aligned to the right
                boxShadow: 1,
              }}
            >
              <Typography
                sx={{ fontSize: 12, color: "#555", mb: 0.5 }}
                component="div"
              >
                <strong>{msg.sender}</strong> | {new Date(msg.timestamp).toLocaleString()}
              </Typography>
              <Typography>{msg.text}</Typography>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Paper>

        <Box sx={{ display: "flex", gap: 1, bgcolor: '#fff', p: 1, borderRadius: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Type your message"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            sx={{
              backgroundColor: "#A8E9F3",
              color: "#00695c",
              borderRadius: '10px',
              minWidth: '100px',
              "&:hover": {
                backgroundColor: "#A8E9F3",
              },
            }}
          >
            SEND
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default Chat;
